import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import StatusBadge from '@/components/base/StatusBadge';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import { quoteStatuses, projectCategories } from '@/types';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

export default function Quotes() {
  const navigate = useNavigate();
  const { openCreate, openEdit, openDelete } = useApp();
  const { quotes, customerName } = useData();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return quotes.filter((qt) => {
      const matchesQuery =
        !q ||
        qt.number.toLowerCase().includes(q) ||
        qt.projectName.toLowerCase().includes(q) ||
        customerName(qt.customerId).toLowerCase().includes(q);
      const matchesStatus = !status || qt.status === status;
      const matchesCategory = !category || qt.category === category;
      return matchesQuery && matchesStatus && matchesCategory;
    });
  }, [query, status, category, quotes, customerName]);

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="Teklifler"
        description={`${filtered.length} teklif listeleniyor`}
        actions={
          <button
            type="button"
            onClick={() => openCreate('quote')}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-lg" /> Yeni Teklif
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Teklif no, proje adı, müşteri ara..."
            className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Durumlar</option>
          {quoteStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 max-w-[220px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Kategoriler</option>
          {projectCategories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Teklif bulunamadı" description="Filtreleri değiştirerek tekrar deneyin." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Teklif No</th>
                  <th className="px-4 py-3">Müşteri</th>
                  <th className="px-4 py-3">Proje</th>
                  <th className="px-4 py-3">Kategori</th>
                  <th className="px-4 py-3 text-right">Tutar</th>
                  <th className="px-4 py-3 text-right">Tahmini Kâr</th>
                  <th className="px-4 py-3">Geçerlilik</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3">Sonraki Takip</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((qt) => (
                  <tr
                    key={qt.id}
                    onClick={() => navigate(`/quotes/${qt.id}`)}
                    className="cursor-pointer border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60"
                  >
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-foreground-900">{qt.number}</p>
                      <p className="text-xs text-foreground-500">{formatDate(qt.date)}</p>
                    </td>
                    <td className="px-4 py-3.5 text-foreground-700">{customerName(qt.customerId)}</td>
                    <td className="px-4 py-3.5 font-medium text-foreground-900">{qt.projectName}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{qt.category}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(qt.amount)}</td>
                    <td className="px-4 py-3.5 text-right font-semibold text-primary-700">{formatTL(qt.estimatedProfit)}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(qt.validityDate)}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={qt.status} /></td>
                    <td className="px-4 py-3.5 text-foreground-600">{qt.nextFollowUp !== '-' ? formatDate(qt.nextFollowUp) : '-'}</td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('quote', qt)} onDelete={() => openDelete('quote', qt.id, qt.number)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}